import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Grid, Typography, IconButton, Tooltip, Collapse, useMediaQuery, useTheme, FormGroup, FormLabel } from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy, Delete } from '@mui/icons-material';
import dayjs from 'dayjs';
import Button, { ButtonType } from '@components/topstep/button';
import { useApi } from '@/contexts/ApiContext';
import { RevokeUserApiKeyRequest, UserApiKeyModel } from '@/api/userApi';
import { toast } from 'react-toastify';
import { logException } from '@/helpers/exceptionHelper';
import { useAuth } from '@/contexts/AuthContext';
import HelpMessage from '../helpMessage';

const ApiSettings: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<UserApiKeyModel[]>([]);
  const [revealedKeyId, setRevealedKeyId] = useState<string | null>(null);
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);

  const { userApi } = useApi();
  const { userInfo } = useAuth();

  useEffect(() => {
    userApi.getApiKeys().then((response) => {
      if (response) {
        setApiKeys(response);
      }
    });
  }, [userApi]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Common card styling
  const cardStyle = {
    height: '100%',
    borderRadius: '8px',
    backgroundImage: 'none',
    backgroundColor: isSmallScreen ? '#FFFFFF10' : '#1c1e23',
    overflowY: 'auto',
    maxHeight: '100%',
    ...(isSmallScreen && {
      boxShadow: 'none',
      overflowY: 'unset',
      maxHeight: 'unset'
    })
  };

  const onClickLink = useCallback(() => {
    userApi
      .getDashboardLinkUrl()
      .then((response) => {
        if (response.error) {
          console.error('Error getting dashboard link', response.error);
          toast('Error getting dashboard link', { type: 'error', hideProgressBar: true });
          return;
        }

        const encodedName = encodeURIComponent(userInfo?.userName || 'User');

        window.open(response.url + `&userName=${encodedName}`, '_blank');
      })
      .catch((e) => {
        logException(e, 'Error getting dashboard link');
        toast('Error getting dashboard link', { type: 'error', hideProgressBar: true });
      });
  }, []);

  const onClickAddKey = () => {
    const promise = new Promise((resolve, reject) => {
      userApi
        .createApiKey()
        .then((response) => {
          if (!response.success) {
            reject(response.errorMessage);
          } else {
            // Refetch the API keys after creating a new one
            userApi.getApiKeys().then((newResponse) => {
              if (newResponse) {
                setApiKeys(newResponse);
              }
            });
            resolve(response);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });

    toast.promise(promise, {
      pending: 'Creating API Key...',
      success: 'API Key created successfully!',
      error: {
        render({ data }) {
          // When the promise rejects, data will contain the error
          return <span>Error creating key: {data.toString()}</span>;
        }
      }
    });
  };

  const handleReveal = (id: string) => {
    setRevealedKeyId((prev) => (prev === id ? null : id));
  };

  const handleCopy = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success('API Key copied to clipboard!');
  };

  const handleRevoke = (id: string) => {
    setRevokingKeyId(id);
    userApi
      .revokeApiKey(
        new RevokeUserApiKeyRequest({
          apiKey: id
        })
      )
      .then((response) => {
        if (response.success) {
          toast.success('API Key revoked.');
          // Refetch keys
          userApi.getApiKeys().then((newResponse) => {
            if (newResponse) setApiKeys(newResponse);
          });
        } else {
          toast.error(response.errorMessage || 'Failed to revoke API Key.');
        }
      })
      .catch(() => {
        toast.error('Error revoking API Key.');
      })
      .finally(() => {
        setRevokingKeyId(null);
      });
  };

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: isSmallScreen ? '#1c1e23' : 'transparent',
        overflowY: isSmallScreen ? 'auto' : 'hidden',
        maxHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        height: isSmallScreen ? 'calc(100dvh - 120px)' : 'calc(100vh - 200px)'
      }}
    >
      <Grid
        container
        spacing={3}
        sx={{
          maxHeight: isSmallScreen ? undefined : 'calc(100% - 20px)'
        }}
      >
        {/* Panel 1 */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            height: isSmallScreen ? 'auto' : '100%',
            maxHeight: '100%'
          }}
        >
          <Card
            elevation={isSmallScreen ? 0 : 2}
            sx={{
              ...cardStyle,
              height: isSmallScreen ? 'auto' : '100%',
              overflow: isSmallScreen ? 'visible' : 'auto'
            }}
          >
            <CardHeader title='API Keys' titleTypographyProps={{ variant: 'h6' }} sx={{ pb: 1 }} />
            <CardContent>
              <Typography sx={{ fontSize: '1.15rem', fontWeight: 500 }}>
                Copy your API key below and store it in a secure location. To authenticate, visit our API documentation{' '}
                <a href="https://gateway.docs.projectx.com" target="_blank" sx={{ color: '#ff6b35', textDecoration: 'underline', fontWeight: 600 }}>
                  here
                </a>{' '}
                and follow the directions under Getting Started.
              </Typography>
              <Box sx={{ mt: 3 }}>
                {apiKeys.length === 0 && (
                  <>
                    <Typography color='text.secondary' align='center'>
                      No API keys created yet.
                    </Typography>
                    <br />
                  </>
                )}
                {apiKeys.map((key) => (
                  <Card
                    key={key.key}
                    variant='outlined'
                    sx={{
                      mb: 2,
                      borderRadius: '8px',
                      background: '#23242a',
                      boxShadow: 'none'
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Typography variant='subtitle2' sx={{ fontWeight: 600, mr: 1, color: 'white' }}>
                          API Key
                        </Typography>
                        <Box>
                          <Tooltip title={revealedKeyId === key.key ? 'Hide API Key' : 'Show API Key'}>
                            <IconButton size='small' onClick={() => handleReveal(key.key)}>
                              {revealedKeyId === key.key ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Copy API Key'>
                            <span>
                              <IconButton size='small' disabled={revealedKeyId !== key.key} onClick={() => handleCopy(key.key)}>
                                <ContentCopy />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='Revoke API Key'>
                            <span>
                              <IconButton size='small' color='error' disabled={revokingKeyId === key.key} onClick={() => handleRevoke(key.key)}>
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Collapse in={revealedKeyId === key.key}>
                        <Box
                          sx={{
                            mt: 1,
                            mb: 1,
                            p: 1,
                            background: '#18191d',
                            borderRadius: '4px',
                            wordBreak: 'break-all',
                            fontFamily: 'monospace',
                            fontSize: '1.1em'
                          }}
                        >
                          {key.key}
                        </Box>
                      </Collapse>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                          Created: {key.createdAt ? dayjs(key.createdAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Last Used: {key.lastUsedAt ? dayjs(key.lastUsedAt).format('YYYY-MM-DD HH:mm:ss') : 'Never'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
              <Button buttonType={ButtonType.gold} style={{ color: 'white', width: '100%' }} onClick={onClickAddKey}>
                Add API Key
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Panel 2 */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            height: isSmallScreen ? 'auto' : '100%',
            maxHeight: '100%'
          }}
        >
          <Card
            elevation={isSmallScreen ? 0 : 2}
            sx={{
              ...cardStyle,
              height: isSmallScreen ? 'auto' : '100%',
              overflow: isSmallScreen ? 'visible' : 'auto'
            }}
          >
            <CardHeader title='ProjectX Linking' titleTypographyProps={{ variant: 'h6' }} sx={{ pb: 1 }} />
            <CardContent>
              <FormGroup>
                <FormLabel>
                  Link to ProjectX Dashboard
                  <HelpMessage message='Linking your account allows sharing your subscription across multiple products.' />
                </FormLabel>
                <Button onClick={onClickLink} buttonType={ButtonType.gold} style={{ margin: '1em 0', color: 'white' }}>
                  Link
                </Button>
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiSettings;
